(() => {
  const STORAGE_PREFIX = 'acb-dismissed:';
  const SMOOTH_EASE = 'cubic-bezier(0.33, 1, 0.68, 1)';
  const OPEN_MS = 700;
  const CLOSE_MS = 550;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function animationsEnabled(root) {
    return root.getAttribute('data-acb-animate') !== 'false' && !prefersReducedMotion();
  }

  function clearStoredDismissals() {
    try {
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.indexOf(STORAGE_PREFIX) === 0) keys.push(key);
      }
      keys.forEach((key) => sessionStorage.removeItem(key));
    } catch (e) {
      /* ignore */
    }
  }

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function clearInlineMotion(root) {
    root.style.height = '';
    root.style.minHeight = '';
    root.style.maxHeight = '';
    root.style.overflow = '';
    root.style.opacity = '';
    root.style.transition = '';
  }

  function setStickyOffset(px) {
    document.documentElement.style.setProperty('--acb-sticky-offset', `${px}px`);
  }

  function pauseStickyObserver(root) {
    if (root._acbResizeObserver) root._acbResizeObserver.disconnect();
  }

  function resumeStickyObserver(root) {
    if (root._acbResizeObserver && root.isConnected) {
      root._acbResizeObserver.observe(root);
    }
  }

  function markOpened(root) {
    root.setAttribute('data-acb-opened', '');
    clearInlineMotion(root);
    resumeStickyObserver(root);
    root.querySelectorAll('[data-acb-marquee]').forEach(fillMarquee);
  }

  function afterPaint(callback) {
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
  }

  function animatePushOpen(root) {
    if (!animationsEnabled(root)) {
      markOpened(root);
      return;
    }

    const natural = root.scrollHeight;
    if (!natural) {
      markOpened(root);
      return;
    }

    pauseStickyObserver(root);
    if (root.classList.contains('acb--sticky')) {
      setStickyOffset(natural);
    }

    root.style.overflow = 'hidden';
    root.style.minHeight = '0px';
    root.style.height = '0px';
    root.style.opacity = '1';
    void root.offsetHeight;

    afterPaint(() => {
      root.style.transition = `height ${OPEN_MS}ms ${SMOOTH_EASE}`;
      root.style.height = `${natural}px`;
    });

    const finish = (event) => {
      if (event && event.propertyName && event.propertyName !== 'height') return;
      root.removeEventListener('transitionend', finish);
      markOpened(root);
    };

    root.addEventListener('transitionend', finish);
  }

  function animatePushClose(root, onDone) {
    if (!animationsEnabled(root)) {
      onDone();
      return;
    }

    const height = root.offsetHeight;
    pauseStickyObserver(root);
    if (root.classList.contains('acb--sticky')) {
      setStickyOffset(0);
    }

    root.classList.add('is-collapsing');
    root.style.overflow = 'hidden';
    root.style.minHeight = '0px';
    root.style.maxHeight = 'none';
    root.style.height = `${height}px`;
    void root.offsetHeight;

    afterPaint(() => {
      root.style.transition = `height ${CLOSE_MS}ms ${SMOOTH_EASE}, opacity ${CLOSE_MS}ms ${SMOOTH_EASE}`;
      root.style.height = '0px';
      root.style.opacity = '0';
    });

    const finish = (event) => {
      if (event && event.propertyName && event.propertyName !== 'height') return;
      root.removeEventListener('transitionend', finish);
      clearInlineMotion(root);
      onDone();
    };

    root.addEventListener('transitionend', finish);
  }

  function stripEditorAttrs(node) {
    if (!node) return;
    if (node instanceof Element) {
      Array.from(node.attributes).forEach((attr) => {
        if (attr.name.indexOf('data-shopify') === 0 || attr.name === 'data-aid') {
          node.removeAttribute(attr.name);
        }
      });
    }
    if (node.querySelectorAll) {
      node.querySelectorAll('*').forEach((el) => {
        Array.from(el.attributes).forEach((attr) => {
          if (attr.name.indexOf('data-shopify') === 0 || attr.name === 'data-aid') {
            el.removeAttribute(attr.name);
          }
        });
      });
    }
  }

  function measureWidth(el) {
    return el.getBoundingClientRect().width;
  }

  function fillMarquee(marquee) {
    const track = marquee.querySelector('.acb__marquee-track');
    const group = marquee.querySelector('[data-acb-marquee-group]');
    if (!track || !group || !group.children.length) return;

    const targetWidth = Math.max(
      marquee.clientWidth,
      measureWidth(marquee),
      (marquee.closest('[data-acb]') || document.documentElement).clientWidth,
      320
    );
    if (targetWidth < 40) return;

    if (!marquee._acbMarqueeSeed) {
      const unique = [];
      const seen = {};
      const separator = marquee.getAttribute('data-acb-marquee-sep');
      Array.from(group.querySelectorAll('.acb__marquee-item')).forEach((item) => {
        const text = (item.textContent || '').trim();
        if (!text || seen[text]) return;
        seen[text] = true;
        const itemClone = item.cloneNode(true);
        stripEditorAttrs(itemClone);
        unique.push(itemClone);
        if (separator) {
          const sep = document.createElement('span');
          sep.className = 'acb__marquee-sep';
          sep.textContent = separator;
          unique.push(sep);
        }
      });
      marquee._acbMarqueeSeed = unique;
    }

    const seed = marquee._acbMarqueeSeed;
    if (!seed.length) return;

    track.style.animation = 'none';

    Array.from(track.querySelectorAll('.acb__marquee-group')).forEach((node, index) => {
      if (index > 0) node.remove();
    });

    group.innerHTML = '';
    seed.forEach((node) => group.appendChild(node.cloneNode(true)));

    let guard = 0;
    let lastWidth = 0;
    while (measureWidth(group) < targetWidth && guard < 60) {
      seed.forEach((node) => group.appendChild(node.cloneNode(true)));
      const nextWidth = measureWidth(group);
      if (nextWidth <= lastWidth + 0.5) break;
      lastWidth = nextWidth;
      guard += 1;
    }

    const duplicate = group.cloneNode(true);
    stripEditorAttrs(duplicate);
    duplicate.removeAttribute('data-acb-marquee-group');
    duplicate.setAttribute('aria-hidden', 'true');
    track.appendChild(duplicate);

    void track.offsetWidth;
    track.style.animation = '';
  }

  function initMarquee(root) {
    root.querySelectorAll('[data-acb-marquee]').forEach((marquee) => {
      if (marquee.dataset.acbMarqueeReady === '1') return;
      marquee.dataset.acbMarqueeReady = '1';

      const build = () => fillMarquee(marquee);
      afterPaint(build);

      if (typeof ResizeObserver !== 'undefined') {
        let frame = 0;
        const observer = new ResizeObserver(() => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(build);
        });
        observer.observe(marquee);
        const shell = marquee.closest('[data-acb]');
        if (shell) observer.observe(shell);
      }

      window.addEventListener('load', build, { once: true });
    });
  }

  function initCountdown(root) {
    const nodes = root.querySelectorAll('[data-acb-countdown]');
    nodes.forEach((el) => {
      const endsAt = Date.parse(el.getAttribute('data-countdown-end') || '');
      const endedEl = el.querySelector('[data-acb-countdown-ended]');
      const units = el.querySelectorAll('[data-acb-countdown-unit]');
      const days = el.querySelector('[data-acb-days]');
      const hours = el.querySelector('[data-acb-hours]');
      const minutes = el.querySelector('[data-acb-minutes]');
      const seconds = el.querySelector('[data-acb-seconds]');

      if (Number.isNaN(endsAt)) {
        el.hidden = true;
        return;
      }

      const tick = () => {
        const remaining = endsAt - Date.now();
        if (remaining <= 0) {
          units.forEach((unit) => {
            unit.hidden = true;
          });
          if (endedEl) endedEl.hidden = false;
          clearInterval(timer);
          return;
        }
        const totalSeconds = Math.floor(remaining / 1000);
        if (days) days.textContent = String(Math.floor(totalSeconds / 86400));
        if (hours) hours.textContent = pad(Math.floor(totalSeconds / 3600) % 24);
        if (minutes) minutes.textContent = pad(Math.floor(totalSeconds / 60) % 60);
        if (seconds) seconds.textContent = pad(totalSeconds % 60);
      };

      tick();
      const timer = setInterval(tick, 1000);
    });
  }

  function dismissBanner(root) {
    if (root.classList.contains('is-collapsing') || root.hidden || root.classList.contains('is-dismissed')) {
      return;
    }

    if (root._acbAutoHideRaf) {
      cancelAnimationFrame(root._acbAutoHideRaf);
      root._acbAutoHideRaf = 0;
    }

    animatePushClose(root, () => {
      root.hidden = true;
      root.classList.add('is-dismissed');
      root.classList.remove('is-collapsing');
      root.dispatchEvent(new CustomEvent('acb:dismissed', { bubbles: true }));
    });
  }

  function initDismiss(root) {
    root.querySelectorAll('[data-acb-dismiss]').forEach((btn) => {
      btn.addEventListener('click', () => dismissBanner(root));
    });
  }

  function syncStickyOffset(root) {
    if (!root.classList.contains('acb--sticky')) return;

    const apply = () => {
      if (root.classList.contains('is-collapsing') || !root.hasAttribute('data-acb-opened')) return;
      const height = root.hidden ? 0 : root.offsetHeight;
      setStickyOffset(height);
    };

    if (typeof ResizeObserver !== 'undefined') {
      root._acbResizeObserver = new ResizeObserver(apply);
      if (root.hasAttribute('data-acb-opened')) {
        root._acbResizeObserver.observe(root);
        apply();
      }
    } else if (root.hasAttribute('data-acb-opened')) {
      apply();
    }

    root.addEventListener(
      'acb:dismissed',
      () => {
        setStickyOffset(0);
      },
      { once: true }
    );
  }

  function initAutoHide(root) {
    if (root.getAttribute('data-acb-auto-hide') !== 'true') return;
    if (root.hidden || root.classList.contains('is-dismissed')) return;

    const progressEls = root.querySelectorAll('[data-acb-dismiss-progress]');
    if (!progressEls.length) return;

    const seconds = Math.max(
      1,
      parseInt(root.getAttribute('data-acb-auto-hide-seconds') || '30', 10)
    );
    const durationMs = prefersReducedMotion() ? 0 : seconds * 1000;

    const startTimer = () => {
      if (durationMs <= 0) {
        progressEls.forEach((el) => {
          el.style.strokeDashoffset = '100';
        });
        dismissBanner(root);
        return;
      }

      const start = performance.now();

      const tick = (now) => {
        if (root.hidden || root.classList.contains('is-dismissed') || root.classList.contains('is-collapsing')) {
          root._acbAutoHideRaf = 0;
          return;
        }

        const t = Math.min(1, (now - start) / durationMs);
        const offset = t * 100;
        progressEls.forEach((el) => {
          el.style.strokeDashoffset = String(offset);
        });

        if (t >= 1) {
          root._acbAutoHideRaf = 0;
          dismissBanner(root);
          return;
        }

        root._acbAutoHideRaf = requestAnimationFrame(tick);
      };

      root._acbAutoHideRaf = requestAnimationFrame(tick);
    };

    if (root.hasAttribute('data-acb-opened')) {
      startTimer();
      return;
    }

    const observer = new MutationObserver(() => {
      if (!root.hasAttribute('data-acb-opened')) return;
      observer.disconnect();
      startTimer();
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-acb-opened'] });
  }

  function initSection(root) {
    if (!root || root.dataset.acbReady === '1') return;
    root.dataset.acbReady = '1';
    initDismiss(root);
    syncStickyOffset(root);
    initMarquee(root);
    if (!root.hidden) {
      animatePushOpen(root);
      initCountdown(root);
      initAutoHide(root);
    }
  }

  function scan(scope) {
    (scope || document).querySelectorAll('[data-acb]').forEach(initSection);
  }

  clearStoredDismissals();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => {
    scan(event.target);
  });
})();
